<template>
  <CustomModal
    :id="`item-description-changed-${comment.id}`"
    modalClass="custom-modal_item-description"
    width="75%"
  >
    <template #header>
      <div class="item-content-changed__header">
        <CommentOwner
          :user="comment.owner"
          userNameWeight="400"
        >
          <template #username="{ userName }">
            {{ userName }} <span class="heading-level-3">{{ comment.subtitleLabel }}</span>
          </template>
        </CommentOwner>
        <span class="body-text-large-400">{{ comment.timeAndDateReversed }}</span>
      </div>
    </template>
    <template #body>
      <span class="item-content-changed__content">
        <span class="item-content-changed__label">Before:</span>
        <div class="item-content-changed__description">
          <Editor
            id="description"
            type="read"
            :value="beforeDescription"
            :extensions="extensions"
          />
        </div>
      </span>
      <span class="item-content-changed__content">
        <span class="item-content-changed__label">After:</span>
        <div class="item-content-changed__description">
          <Editor
            id="description"
            type="read"
            :value="afterDescription"
            :isAttachmentResizable="false"
            :extensions="extensions"
          />
        </div>
      </span>
    </template>

    <template #footer="{ hide }">
      <ControlButtons
        :hideCancel="true"
        @save="hide"
      >
        <template #save> Ok </template>
      </ControlButtons>
    </template>
  </CustomModal>
</template>

<script lang="ts">
import { Options, Vue } from "vue-class-component"
import { Prop } from "vue-property-decorator"
import TaskItem from "@tiptap/extension-task-item"
import TaskList from "@tiptap/extension-task-list"
import { Action, Getter } from "s-vuex-class"

import ControlButtons from "@/components/ControlButtons/index.vue"
import Editor from "@/components/Editor/index.vue"
import CustomModal from "@/components/Modal/index.vue"
import CommentOwner from "@/components/User/index.vue"

import {
  Comment,
  CommentContentType,
  CommentItemAuditItem,
  Item,
  UploadAttachmentFn
} from "@/models"
import { Attachment } from "@/plugins/extensions/Attachment"
import { ReferenceAllUser, ReferenceAt } from "@/plugins/extensions/reference/referenceAt"
import {
  ReferenceHashEpic,
  ReferenceHashFile,
  ReferenceHashFileLink,
  ReferenceHashIdea,
  ReferenceHashItem,
  ReferenceHashMilestone,
  ReferenceHashProject,
  ReferenceHashSprint,
  ReferenceHashTask,
  ReferenceHashTeam,
  ReferenceHashTip
} from "@/plugins/extensions/reference/referenceHash"
import { ReferenceLink } from "@/plugins/extensions/reference/referenceLink"
import { Actions, Getters } from "@/store"

@Options({
  name: "ModalItemDescriptionChanged",
  components: {
    CustomModal,
    ControlButtons,
    Editor,
    CommentOwner
  }
})
export default class extends Vue {
  @Prop({ required: true }) comment: Comment
  @Getter(Getters.GET_ITEM) item: Item
  @Action(Actions.ATTACHMENT_UPLOAD) attachmentUpload: UploadAttachmentFn

  get extensions() {
    const extensions = [
      Attachment(this.attachmentUpload, null, true),
      ReferenceAt,
      ReferenceHashProject,
      ReferenceHashMilestone,
      ReferenceHashEpic,
      ReferenceHashIdea,
      ReferenceHashTeam,
      ReferenceHashSprint,
      ReferenceHashItem,
      ReferenceHashTask,
      ReferenceHashTip,
      ReferenceHashFile,
      ReferenceHashFileLink,
      ReferenceLink,
      TaskList.configure({
        HTMLAttributes: {
          class: "custom-check-list"
        }
      }),
      TaskItem.configure({
        nested: true,
        HTMLAttributes: {
          class: "custom-check-list__item"
        }
      })
    ]
    if (this.item?.id) {
      extensions.push(ReferenceAllUser)
    }
    return extensions
  }

  get beforeDescription() {
    return this.getHtml(this.comment.before) ?? this.$t("none")
  }

  get afterDescription() {
    return this.getHtml(this.comment.after) ?? this.$t("none")
  }

  getHtml(auditItem: Partial<CommentItemAuditItem> | null) {
    switch (this.comment.type) {
      case CommentContentType.ACCEPTANCE_CRITERIA:
        return auditItem?.acceptanceCriteria?.html
      case CommentContentType.SPEC:
        return auditItem?.spec?.html
      default:
        return auditItem?.structuredDescription?.html
    }
  }
}
</script>
